import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, GraduationCap, Save, ArrowLeft, ShieldCheck, Clock, Camera, MapPin,
    BookOpen, Languages, Calendar, Wallet, FileText, AlertTriangle, CheckCircle2, X,
} from "lucide-react";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import { useTutorAuth } from "../context/TutorAuthContext";
import MultiSelectChips from "../components/MultiSelectChips";
import DataCompletenessMeter from "../components/DataCompletenessMeter";
import TutorFileUpload from "../components/tutor/TutorFileUpload";
import TutorSelfDeleteCard from "../components/tutor/TutorSelfDeleteCard";
import { useMasterList } from "../lib/useMasterList";
import {
    PREFERRED_DAYS, PREFERRED_TIME_WINDOWS, CLASS_DURATIONS, LEARNING_LEVELS, FEE_TYPES,
} from "../lib/aiMatchFields";

const SENSITIVE = new Set([
    "name", "gender", "qualification",
    "bank_account_holder", "bank_account_number", "bank_ifsc", "bank_name",
    "id_proof_document_id",
]);

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500";

export default function TutorProfile() {
    const { tutor, refreshMe } = useTutorAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState({});
    const [pending, setPending] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [completeness, setCompleteness] = useState({ percent: 0, confidence: "Low", missing_critical: [] });
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const photoRef = useRef(null);

    const { items: subjectItems } = useMasterList("subject");
    const { items: gradeItems } = useMasterList("grade");
    const { items: boardItems } = useMasterList("board");
    const { items: languageItems } = useMasterList("language");
    const { items: examItems } = useMasterList("exam_category");
    const { items: currItems } = useMasterList("curriculum_type");
    const { items: countries } = useMasterList("country");

    const fetchProfile = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tutorApi.get("/tutor/me/profile");
            setProfile(data.profile || {});
            setPending(data.pending_changes || []);
            setDocuments(data.documents || []);
            setCompleteness(data.completeness || { percent: 0, confidence: "Low", missing_critical: [] });
            // Pull photo if it exists
            try {
                const meta = await tutorApi.get(`/site-assets/${data.photo_asset_key}`);
                if (meta.data?.exists) {
                    setPhotoUrl(`${process.env.REACT_APP_BACKEND_URL}/api${meta.data.raw_url}`);
                }
            } catch { /* no photo yet */ }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    // Track which sensitive fields have a pending change
    const pendingByField = useMemo(() => {
        const m = {};
        for (const p of pending) m[p.field] = p;
        return m;
    }, [pending]);

    const save = async (patch) => {
        setSaving(true);
        try {
            const { data } = await tutorApi.patch("/tutor/me/profile", patch);
            setProfile(data.profile || {});
            setCompleteness(data.completeness || completeness);
            if (data.pending?.length) {
                toast.success(`${data.pending.length} change(s) sent to admin for approval`);
                // Refresh pending list
                const refreshed = await tutorApi.get("/tutor/me/profile");
                setPending(refreshed.data.pending_changes || []);
            } else if (data.applied?.length) {
                toast.success("Saved");
            }
            return data;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
            throw err;
        } finally { setSaving(false); }
    };

    const updateField = (field, value) => {
        // Optimistic UI for non-sensitive; for sensitive show pending state immediately
        setProfile((p) => ({ ...p, [field]: value }));
        save({ [field]: value });
    };

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setPhotoUploading(true);
        try {
            const { data } = await tutorApi.post("/tutor/me/photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotoUrl(`${process.env.REACT_APP_BACKEND_URL}/api${data.raw_url}`);
            toast.success("Photo updated");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setPhotoUploading(false);
            if (photoRef.current) photoRef.current.value = "";
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    const sensitiveBadge = (field) => {
        const p = pendingByField[field];
        if (!p) return null;
        return (
            <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1 ml-1"
                title={`Pending approval. New value: ${JSON.stringify(p.new_value)}`}
                data-testid={`pending-${field}`}>
                <Clock className="w-3 h-3" /> Awaiting approval
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-12">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <button onClick={() => navigate("/tutor/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#1E3A8A]" data-testid="tutor-profile-back">
                        <ArrowLeft className="w-4 h-4" /> Dashboard
                    </button>
                    <div className="flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-[#1E3A8A]" />
                        <span className="font-semibold text-sm">My Profile</span>
                    </div>
                    <div className="w-16 text-right text-xs text-slate-500">
                        {saving && <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving</span>}
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                <DataCompletenessMeter percent={completeness.percent} kind="tutor" testid="tutor-profile-meter" />
                {completeness.missing_critical?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="tutor-profile-missing-critical">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <strong>Complete these to improve AI recommendations:</strong>{" "}
                            {completeness.missing_critical.join(", ")}.
                        </div>
                    </div>
                )}

                {/* Photo + Identity */}
                <Section title="Identity & Photo" icon={Camera}>
                    <div className="flex items-start gap-4">
                        <div className="shrink-0">
                            <div className="w-24 h-24 rounded-full border-2 border-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center"
                                data-testid="tutor-profile-photo">
                                {photoUrl ? (
                                    <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <Camera className="w-8 h-8 text-slate-400" />
                                )}
                            </div>
                            <button type="button" onClick={() => photoRef.current?.click()}
                                disabled={photoUploading}
                                data-testid="tutor-profile-photo-upload"
                                className="mt-2 w-full text-xs text-[#1E3A8A] hover:underline disabled:opacity-60">
                                {photoUploading ? "Uploading…" : photoUrl ? "Replace photo" : "Upload photo"}
                            </button>
                            <input ref={photoRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="tutor-profile-photo-input" />
                        </div>
                        <div className="flex-1 space-y-3">
                            <Field label={<>Full name {sensitiveBadge("name")}</>}>
                                <input type="text" defaultValue={profile.name || ""}
                                    onBlur={(e) => e.target.value !== (profile.name || "") && updateField("name", e.target.value)}
                                    data-testid="tutor-profile-name" className={inputCls} placeholder="Your full name" />
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label={<>Gender {sensitiveBadge("gender")}</>}>
                                    <select value={profile.gender || ""} onChange={(e) => updateField("gender", e.target.value)}
                                        data-testid="tutor-profile-gender" className={inputCls}>
                                        <option value="">Not specified</option>
                                        <option value="female">Female</option>
                                        <option value="male">Male</option>
                                        <option value="other">Other</option>
                                    </select>
                                </Field>
                                <Field label={<>Qualification {sensitiveBadge("qualification")}</>}>
                                    <input type="text" defaultValue={profile.qualification || ""}
                                        onBlur={(e) => e.target.value !== (profile.qualification || "") && updateField("qualification", e.target.value)}
                                        data-testid="tutor-profile-qualification" className={inputCls} placeholder="e.g. M.Sc. Mathematics" />
                                </Field>
                            </div>
                            <Field label="Experience">
                                <input type="text" defaultValue={profile.experience || ""}
                                    onBlur={(e) => e.target.value !== (profile.experience || "") && updateField("experience", e.target.value)}
                                    data-testid="tutor-profile-experience" className={inputCls} placeholder="e.g. 8 years teaching Class 9-12" />
                            </Field>
                            <Field label="About / Bio">
                                <textarea defaultValue={profile.bio || ""} rows={3}
                                    onBlur={(e) => e.target.value !== (profile.bio || "") && updateField("bio", e.target.value)}
                                    data-testid="tutor-profile-bio" className={`${inputCls} resize-none`}
                                    placeholder="Tell parents about your teaching style, approach and achievements." />
                            </Field>
                        </div>
                    </div>
                </Section>

                {/* Teaching coverage */}
                <Section title="Teaching Coverage" icon={BookOpen}>
                    <Field label="Subjects you teach">
                        <MultiSelectChips options={subjectItems.map((s) => ({ value: s.value }))}
                            value={profile.subjects_list || []}
                            onChange={(v) => updateField("subjects_list", v)}
                            placeholder="Search subjects…" testid="tutor-profile-subjects" />
                    </Field>
                    <Field label="Grades / Classes you teach">
                        <MultiSelectChips options={gradeItems.map((g) => ({ value: g.value }))}
                            value={profile.grades_list || []} onChange={(v) => updateField("grades_list", v)}
                            placeholder="Search grades…" testid="tutor-profile-grades" />
                    </Field>
                    <Field label="Boards">
                        <MultiSelectChips options={boardItems.map((b) => ({ value: b.value }))}
                            value={profile.boards_list || []} onChange={(v) => updateField("boards_list", v)}
                            placeholder="Search boards…" testid="tutor-profile-boards" />
                    </Field>
                    <Field label="Languages of instruction">
                        <MultiSelectChips options={languageItems.map((l) => ({ value: l.value }))}
                            value={profile.languages || []} onChange={(v) => updateField("languages", v)}
                            placeholder="Search languages…" testid="tutor-profile-languages" />
                    </Field>
                    <Field label="Exam categories you coach">
                        <MultiSelectChips options={examItems.map((e) => ({ value: e.value }))}
                            value={profile.exam_categories || []} onChange={(v) => updateField("exam_categories", v)}
                            placeholder="Search exams…" testid="tutor-profile-exams" />
                    </Field>
                    <Field label="Curriculum types you handle">
                        <MultiSelectChips options={currItems.map((c) => ({ value: c.value }))}
                            value={profile.curriculum_types || []} onChange={(v) => updateField("curriculum_types", v)}
                            placeholder="Search curricula…" testid="tutor-profile-curriculum" />
                    </Field>
                    <Field label="Learning support levels">
                        <MultiSelectChips options={LEARNING_LEVELS.map((l) => ({ value: l.value, label: l.label }))}
                            value={profile.learning_support_levels || []} onChange={(v) => updateField("learning_support_levels", v)}
                            placeholder="Search support levels…" testid="tutor-profile-levels" />
                    </Field>
                </Section>

                {/* Location & Mode */}
                <Section title="Location & Mode" icon={MapPin}>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Mode">
                            <select value={profile.mode || ""} onChange={(e) => updateField("mode", e.target.value)}
                                data-testid="tutor-profile-mode" className={inputCls}>
                                <option value="">—</option>
                                <option value="Home Tuition">Home Tuition</option>
                                <option value="Online">Online</option>
                                <option value="Both">Both</option>
                            </select>
                        </Field>
                        <Field label="Country">
                            <input list="tutor-countries" type="text" defaultValue={profile.country || ""}
                                onBlur={(e) => e.target.value !== (profile.country || "") && updateField("country", e.target.value)}
                                data-testid="tutor-profile-country" className={inputCls} placeholder="e.g. India" />
                            <datalist id="tutor-countries">{countries.map((c) => <option key={c.id} value={c.value} />)}</datalist>
                        </Field>
                        <Field label="State">
                            <input type="text" defaultValue={profile.state || ""}
                                onBlur={(e) => e.target.value !== (profile.state || "") && updateField("state", e.target.value)}
                                data-testid="tutor-profile-state" className={inputCls} placeholder="e.g. Madhya Pradesh" />
                        </Field>
                        <Field label="City">
                            <input type="text" defaultValue={profile.city || ""}
                                onBlur={(e) => e.target.value !== (profile.city || "") && updateField("city", e.target.value)}
                                data-testid="tutor-profile-city" className={inputCls} placeholder="e.g. Jabalpur" />
                        </Field>
                        <Field label="Permanent PIN (single)">
                            <input type="text" defaultValue={profile.permanent_pin_code || ""}
                                onBlur={(e) => e.target.value !== (profile.permanent_pin_code || "") && updateField("permanent_pin_code", e.target.value)}
                                data-testid="tutor-profile-perm-pin" className={inputCls} placeholder="e.g. 482002" />
                        </Field>
                        <Field label="Max travel (km)">
                            <input type="number" min="0" max="200" defaultValue={profile.max_travel_km || ""}
                                onBlur={(e) => {
                                    const n = e.target.value ? Number(e.target.value) : null;
                                    if (n !== (profile.max_travel_km ?? null)) updateField("max_travel_km", n);
                                }}
                                data-testid="tutor-profile-max-travel" className={inputCls} placeholder="e.g. 15" />
                        </Field>
                    </div>
                    <Field label="Teaching PIN codes (multi)">
                        <MultiSelectChips options={(profile.teaching_pin_codes || []).map((v) => ({ value: v }))}
                            value={profile.teaching_pin_codes || []} onChange={(v) => updateField("teaching_pin_codes", v)}
                            placeholder="Add PIN codes you teach in…" testid="tutor-profile-teaching-pins" allowCreate />
                    </Field>
                    <Field label="Travel areas / colonies (multi)">
                        <MultiSelectChips options={(profile.travel_areas || []).map((v) => ({ value: v }))}
                            value={profile.travel_areas || []} onChange={(v) => updateField("travel_areas", v)}
                            placeholder="Add area names…" testid="tutor-profile-travel-areas" allowCreate />
                    </Field>
                </Section>

                {/* Schedule & Availability */}
                <Section title="Schedule & Availability" icon={Calendar}>
                    <Field label="Availability status">
                        <select value={profile.availability_status || "available"} onChange={(e) => updateField("availability_status", e.target.value)}
                            data-testid="tutor-profile-availability-status" className={inputCls}>
                            <option value="available">Available</option>
                            <option value="busy">Busy</option>
                            <option value="inactive">Inactive (paused)</option>
                        </select>
                    </Field>
                    <Field label="Preferred days">
                        <MultiSelectChips options={PREFERRED_DAYS} value={profile.preferred_days || []}
                            onChange={(v) => updateField("preferred_days", v)}
                            placeholder="Search days…" testid="tutor-profile-pref-days" />
                    </Field>
                    <Field label="Preferred time windows">
                        <MultiSelectChips options={PREFERRED_TIME_WINDOWS} value={profile.preferred_time_slots || []}
                            onChange={(v) => updateField("preferred_time_slots", v)}
                            placeholder="Search time windows…" testid="tutor-profile-pref-time" />
                    </Field>
                    <Field label="Class durations supported (min)">
                        <MultiSelectChips
                            options={CLASS_DURATIONS.map((d) => ({ value: String(d.value), label: `${d.value} min` }))}
                            value={(profile.class_duration_supported || []).map(String)}
                            onChange={(arr) => updateField("class_duration_supported", arr.map(Number))}
                            placeholder="Search durations…" testid="tutor-profile-durations" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Max students at a time">
                            <input type="number" min="0" max="100" defaultValue={profile.max_students_capacity || ""}
                                onBlur={(e) => {
                                    const n = e.target.value ? Number(e.target.value) : null;
                                    if (n !== (profile.max_students_capacity ?? null)) updateField("max_students_capacity", n);
                                }}
                                data-testid="tutor-profile-max-students" className={inputCls} placeholder="e.g. 8" />
                        </Field>
                        <Field label="Demo class available?">
                            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-2.5">
                                <input type="checkbox" checked={!!profile.demo_available} onChange={(e) => updateField("demo_available", e.target.checked)}
                                    data-testid="tutor-profile-demo-available" className="w-4 h-4 accent-emerald-600" />
                                Yes, I&rsquo;m available for free demo classes.
                            </label>
                        </Field>
                    </div>
                </Section>

                {/* Fee */}
                <Section title="Fee Expectations" icon={Wallet}>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Min (₹)">
                            <input type="number" min="0" defaultValue={profile.fee_expectation_min || ""}
                                onBlur={(e) => {
                                    const n = e.target.value ? Number(e.target.value) : null;
                                    if (n !== (profile.fee_expectation_min ?? null)) updateField("fee_expectation_min", n);
                                }}
                                data-testid="tutor-profile-fee-min" className={inputCls} placeholder="4000" />
                        </Field>
                        <Field label="Max (₹)">
                            <input type="number" min="0" defaultValue={profile.fee_expectation_max || ""}
                                onBlur={(e) => {
                                    const n = e.target.value ? Number(e.target.value) : null;
                                    if (n !== (profile.fee_expectation_max ?? null)) updateField("fee_expectation_max", n);
                                }}
                                data-testid="tutor-profile-fee-max" className={inputCls} placeholder="8000" />
                        </Field>
                        <Field label="Fee Type">
                            <select value={profile.fee_expectation_type || "monthly"} onChange={(e) => updateField("fee_expectation_type", e.target.value)}
                                data-testid="tutor-profile-fee-type" className={inputCls}>
                                {FEE_TYPES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </Field>
                    </div>
                </Section>

                {/* Bank details (optional, sensitive) */}
                <Section title="Bank Details (optional)" icon={Wallet}>
                    <p className="text-xs text-slate-500 -mt-1 mb-2">
                        Used only for tuition payouts. Optional — required only when you have active ongoing tuitions.
                        All bank-detail edits go to admin for verification.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label={<>Account holder name {sensitiveBadge("bank_account_holder")}</>}>
                            <input type="text" defaultValue={profile.bank_account_holder || ""}
                                onBlur={(e) => e.target.value !== (profile.bank_account_holder || "") && updateField("bank_account_holder", e.target.value)}
                                data-testid="tutor-profile-bank-holder" className={inputCls} placeholder="As per bank records" />
                        </Field>
                        <Field label={<>Bank name {sensitiveBadge("bank_name")}</>}>
                            <input type="text" defaultValue={profile.bank_name || ""}
                                onBlur={(e) => e.target.value !== (profile.bank_name || "") && updateField("bank_name", e.target.value)}
                                data-testid="tutor-profile-bank-name" className={inputCls} placeholder="e.g. SBI, HDFC" />
                        </Field>
                        <Field label={<>Account number {sensitiveBadge("bank_account_number")}</>}>
                            <input type="text" defaultValue={profile.bank_account_number_masked || ""}
                                onBlur={(e) => {
                                    if (e.target.value && e.target.value !== (profile.bank_account_number_masked || "")) {
                                        updateField("bank_account_number", e.target.value);
                                    }
                                }}
                                data-testid="tutor-profile-bank-account" className={inputCls} placeholder="Type once to update" />
                        </Field>
                        <Field label={<>IFSC / SWIFT {sensitiveBadge("bank_ifsc")}</>}>
                            <input type="text" defaultValue={profile.bank_ifsc || ""}
                                onBlur={(e) => e.target.value !== (profile.bank_ifsc || "") && updateField("bank_ifsc", e.target.value)}
                                data-testid="tutor-profile-bank-ifsc" className={inputCls} placeholder="e.g. SBIN0001234" />
                        </Field>
                    </div>
                </Section>

                {/* Aadhaar verification (mandatory for lead application access) */}
                <Section title="Aadhaar Verification" icon={ShieldCheck}>
                    <AadhaarPanel profile={profile} onUploaded={fetchProfile} />
                </Section>

                {/* Documents */}
                <Section title="Documents" icon={FileText}>
                    <TutorFileUpload kind="certificate" documents={documents}
                        onUploaded={() => fetchProfile()} onDeleted={() => fetchProfile()}
                        testid="tutor-profile-doc-cert" />
                    <TutorFileUpload kind="id_proof" documents={documents}
                        onUploaded={() => fetchProfile()} onDeleted={() => fetchProfile()}
                        testid="tutor-profile-doc-id" />
                </Section>

                {pending.length > 0 && (
                    <section className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="tutor-profile-pending-summary">
                        <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {pending.length} change{pending.length !== 1 ? "s" : ""} awaiting admin approval
                        </h3>
                        <ul className="text-xs text-amber-900 space-y-1">
                            {pending.map((p) => (
                                <li key={p.id} data-testid={`pending-item-${p.field}`}>
                                    <strong>{p.field}:</strong> &ldquo;{String(p.old_value || "—")}&rdquo; → &ldquo;{String(p.new_value)}&rdquo;
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* V29 — Danger Zone: self-delete tutor profile */}
                <TutorSelfDeleteCard />
            </main>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <h2 className="font-bold text-[#0F172A] inline-flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-500" /> {title}
            </h2>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 inline-flex items-center gap-1">{label}</span>
            {children}
        </label>
    );
}

function AadhaarPanel({ profile, onUploaded }) {
    const verified = Boolean(profile?.aadhaar_verified);
    const frontUploaded = Boolean(profile?.aadhaar_front_uploaded);
    const backUploaded = Boolean(profile?.aadhaar_back_uploaded);
    const note = profile?.aadhaar_verification_note || null;
    const aadhaarNum = profile?.aadhaar_number ? `XXXX XXXX ${String(profile.aadhaar_number).slice(-4)}` : "—";
    return (
        <div className="space-y-3" data-testid="tutor-aadhaar-panel">
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                verified ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                         : "bg-amber-50 border-amber-200 text-amber-900"
            }`} data-testid="tutor-aadhaar-status">
                {verified ? <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                         : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                <div className="text-xs leading-relaxed">
                    <strong>
                        {verified ? "Aadhaar verified" : "Aadhaar pending admin verification"}
                    </strong>
                    <div className="mt-0.5">
                        Number on file: <span className="font-mono">{aadhaarNum}</span>
                    </div>
                    {!verified && (
                        <div className="mt-1">
                            You can apply for tuitions only after admin verifies your Aadhaar.
                            Re-upload below if the previous photo was unclear.
                        </div>
                    )}
                    {note && <div className="mt-1 italic">Admin note: {note}</div>}
                </div>
            </div>
            <AadhaarSideUploader side="front" present={frontUploaded} onUploaded={onUploaded} />
            <AadhaarSideUploader side="back" present={backUploaded} onUploaded={onUploaded} />
        </div>
    );
}

function AadhaarSideUploader({ side, present, onUploaded }) {
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Only image files accepted.");
            if (inputRef.current) inputRef.current.value = "";
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("side", side);
            fd.append("file", file);
            await tutorApi.post("/tutor/me/aadhaar", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Aadhaar (${side}) uploaded — admin will verify shortly.`);
            onUploaded?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };
    return (
        <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`tutor-aadhaar-${side}-row`}>
            <div className="text-sm">
                <span className="font-semibold text-slate-800 capitalize">Aadhaar {side}</span>
                <span className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
                    present ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-500 border border-slate-200"
                }`} data-testid={`tutor-aadhaar-${side}-status`}>
                    {present ? <><CheckCircle2 className="w-3 h-3" /> Uploaded</> : "Not uploaded"}
                </span>
            </div>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid={`tutor-aadhaar-${side}-upload`}
                className="text-xs font-semibold text-[#1E3A8A] hover:underline disabled:opacity-60 inline-flex items-center gap-1"
            >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {present ? "Re-upload" : "Upload"}
            </button>
            <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`tutor-aadhaar-${side}-input`} />
        </div>
    );
}
